
<template>
    <div class="orderDetail">
        <BookingBack :isDark="true" title="Order Details"/>
        <div class="orderData" v-if="orderData != null">
            <div class="serviceInfo">
                <div class="title">Service Info</div>
                <OrderAddress :shopName="supplierInfo.name || ''" :address="displaySupplierAddress" :distance="orderData.distance" :lat="supplierLat" :lng="supplierLng"/>
                <van-divider/>
                <OrderPhone :phone="supplierInfo.link_phone || ''"/>
                <div class="orderList">
                    <PayOrderList v-for="item in orderData.service" :key="item.order_service_id" :list="item" />
                    <van-divider :style="{width:'100%',color: '#333333', borderColor: '#cccccc' }"></van-divider>
                    <div class="totalPrice">Total : ${{ orderData.total_price }}</div>
                </div>
            </div>
            <div class="refundMoneyBanner" v-if="canShowRefundMoney">
                Refunded Amount:¥{{ refundData.refund_money }}
            </div>
            <!-- Refund Request Info -->
            <div class="refundSection">
                <div class="sectionTitle">Refund Request Info</div>
                <div class="refundInfoBox">
                    <div class="refundInfoItem">
                        <div class="label">After-sales Reason</div>
                        <div class="value">{{ refundData ? refundData.apply_desc : '--' }}</div>
                    </div>
                    <div class="refundInfoItem imageItem">
                        <div class="label">After-sales Evidence (uploaded images)</div>
                        <div class="imageList">
                            <template v-if="refundData && refundData.image && refundData.image.length > 0">
                                <van-image
                                    v-for="(img, index) in refundData.image"
                                    :key="img.id"
                                    width="60"
                                    height="60"
                                    fit="cover"
                                    :src="getUrl(img.file_path)"
                                    @click="previewImage(index)"
                                />
                            </template>
                            <template v-else>
                                <div class="emptyImage" v-for="n in 3" :key="n"></div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Platform Resolution - 商家同意时不显示，未申请介入时也不显示 -->
            <div class="refundSection" v-if="!isShopAgreed && isPlatformInvolved">
                <div class="sectionTitle">Platform Resolution</div>
                <div class="resultBox">
                    <div class="resultText" :class="getPlatformStatusClass()">
                        {{ getPlatformStatusText() }}
                    </div>
                    <div class="resultDesc" v-if="refundData && refundData.plate_desc">
                        {{ refundData.plate_desc }}
                    </div>
                </div>
            </div>
            <!-- Merchant Resolution -->
            <div class="refundSection" v-if="refundData && refundData.is_agree">
                <div class="sectionTitle">Merchant Resolution</div>
                <div class="resultBox">
                    <div class="resultText" :class="getShopStatusClass()">
                        {{ getShopStatusText() }}
                    </div>
                    <div class="resultDesc" v-if="refundData.is_agree.value === 20 && refundData.refuse_desc">
                        Rejection Reason: {{ refundData.refuse_desc }}
                    </div>
                </div>
            </div>
        </div>
        <van-skeleton title :row="10" :loading="loading"></van-skeleton>
    </div>
</template>
<script>
import { ImagePreview } from 'vant'
import myMixin from '@/utils/myMixin'
import BookingBack from '@/components/Booking/back.vue'
import OrderAddress from '@/components/Order/address.vue'
import OrderPhone from '@/components/Order/phone.vue'
import PayOrderList from '@/components/Pay/orderList.vue'
import { ensureUserLngLat } from '@/utils/location'
export default {
    name: 'RefundDetail',
    mixins: [myMixin],
    data() {
        return {
            order_refund_id: this.$route.query.order_refund_id,
            supplierAddressFromList: this.$route.query.supplier_address || '',
            refundData: null,
            orderData: null,
            supplierInfo: {},
            loading: true
        }
    },
    components: {
        BookingBack,
        OrderAddress,
        OrderPhone,
        PayOrderList
    },
    computed: {
        supplierLat() {
            const raw = this.supplierInfo.latitude || this.supplierInfo.lat || this.supplierInfo.shop_lat
            const n = Number(raw)
            return Number.isFinite(n) ? n : ""
        },
        supplierLng() {
            const raw = this.supplierInfo.longitude || this.supplierInfo.lng || this.supplierInfo.shop_lng
            const n = Number(raw)
            return Number.isFinite(n) ? n : ""
        },
        displaySupplierAddress() {
            return this.supplierInfo.address || this.supplierAddressFromList || ""
        },
        /**
         * 商家是否同意退款
         * is_agree: 商家审核状态 (0待审核 10已同意 20已拒绝)
         * status: 售后单状态 (0In Progress 10已拒绝 20Completed 30Cancelled)
         * plate_status: 平台介入状态 (10Request Platform Intervention 20同意 30拒绝)
         */
        isShopAgreed() {
            return this.refundData && this.refundData.is_agree && this.refundData.is_agree.value === 10
        },
        /**
         * 平台是否已介入
         * plate_status.value >= 10 表示已Request Platform Intervention
         */
        isPlatformInvolved() {
            return this.refundData && this.refundData.plate_status && this.refundData.plate_status.value >= 10
        },
        /**
         * 退款金额显示条件：
         * - refund_money 字段存在
         * - 商家同意 (is_agree.value === 10) 或 平台同意 (plate_status.value === 20)
         */
        canShowRefundMoney() {
            if (!this.refundData || typeof this.refundData.refund_money === 'undefined' || this.refundData.refund_money === null || this.refundData.refund_money === '') {
                return false
            }
            const shopAgreed = this.refundData.is_agree && Number(this.refundData.is_agree.value) === 10
            const platformAgreed = this.refundData.plate_status && Number(this.refundData.plate_status.value) === 20
            return shopAgreed || platformAgreed
        }
    },
    created() {
        this.getRefundDetail()
    },
    methods: {
        async getRefundDetail() {
            try {
                // Best-effort: fetch/cache user's lng/lat before requesting after-sale detail.
                await ensureUserLngLat().catch(() => {})
                const result = await this.$api.getServiceRefundDetail({ order_refund_id: this.order_refund_id })
                if (result.code === 1 && result.data && result.data.detail) {
                    this.refundData = result.data.detail
                    this.buildOrderSnapshotFromRefund()
                    // Keep backward compatibility: only fallback to order-detail API when refund detail
                    // does not contain enough order data for rendering.
                    if (!this.orderData && this.refundData.order_id) {
                        await this.getOrderDetail(this.refundData.order_id)
                    } else {
                        this.loading = false
                    }
                } else {
                    this.loading = false
                }
            } catch (error) {
                this.loading = false
            }
        },
        buildOrderSnapshotFromRefund() {
            if (!this.refundData) return
            if (this.refundData.supplier) {
                this.supplierInfo = this.refundData.supplier
            }
            const service = this.refundData.orderservice
            if (!service) return
            this.orderData = {
                order_id: this.refundData.order_id,
                shop_supplier_id: this.refundData.shop_supplier_id,
                distance: this.refundData.distance,
                total_price: service.total_price || this.refundData.refund_money || '0.00',
                service: [service]
            }
        },
        async getOrderDetail(order_id) {
            try {
                // Best-effort: fetch/cache user's lng/lat before requesting order detail.
                await ensureUserLngLat().catch(() => {})
                const result = await this.$api.getOrderDetail({ order_id })
                if (result.code === 1) {
                    this.orderData = result.data.order
                    // 获取店铺详情
                    if (this.orderData && this.orderData.shop_supplier_id) {
                        await this.getSupplierDetail(this.orderData.shop_supplier_id)
                    }
                }
            } finally {
                this.loading = false
            }
        },
        async getSupplierDetail(shop_supplier_id) {
            try {
                const result = await this.$api.getSupplierDetail({ shop_supplier_id })
                if (result.code === 1 && result.data && result.data.detail) {
                    this.supplierInfo = result.data.detail
                }
            } catch (e) {
                console.error('Failed to get merchant details:', e)
            }
        },
        previewImage(index) {
            if (!this.refundData || !this.refundData.image) return
            const images = this.refundData.image.map(img => this.getUrl(img.file_path))
            ImagePreview({
                images,
                startPosition: index
            })
        },
        /**
         * 获取平台状态文本
         * plate_status: 平台介入状态
         * - 10: Request Platform Intervention(待处理)
         * - 20: 平台同意退款
         * - 30: 平台拒绝退款
         */
        getPlatformStatusText() {
            if (!this.refundData || !this.refundData.plate_status) return 'Waiting for result'
            const status = this.refundData.plate_status.value
            if (status === 10) return 'Waiting for result'
            if (status === 20) return 'Platform approved refund'
            if (status === 30) return 'Platform rejected refund'
            return this.refundData.plate_status.text || 'Waiting for result'
        },
        /**
         * 获取平台状态样式类
         * plate_status: 平台介入状态
         * - 10: Request Platform Intervention(待处理) -> pending
         * - 20: 平台同意退款 -> approved
         * - 30: 平台拒绝退款 -> rejected
         */
        getPlatformStatusClass() {
            if (!this.refundData || !this.refundData.plate_status) return 'pending'
            const status = this.refundData.plate_status.value
            if (status === 10) return 'pending'
            if (status === 20) return 'approved'
            if (status === 30) return 'rejected'
            return 'pending'
        },
        /**
         * 获取商家状态文本
         * is_agree: 商家审核状态
         * - 0: 待审核
         * - 10: 已同意
         * - 20: 已拒绝
         */
        getShopStatusText() {
            if (!this.refundData || !this.refundData.is_agree) return 'Waiting for merchant processing'
            const status = this.refundData.is_agree.value
            if (status === 0) return 'Waiting for merchant processing'
            if (status === 10) return 'Merchant approved refund'
            if (status === 20) return 'Merchant rejected refund'
            return this.refundData.is_agree.text || 'Waiting for merchant processing'
        },
        /**
         * 获取商家状态样式类
         * is_agree: 商家审核状态
         * - 0: 待审核 -> pending
         * - 10: 已同意 -> shop-approved (#00AD56)
         * - 20: 已拒绝 -> rejected
         */
        getShopStatusClass() {
            if (!this.refundData || !this.refundData.is_agree) return 'pending'
            const status = this.refundData.is_agree.value
            if (status === 0) return 'pending'
            if (status === 10) return 'shop-approved'
            if (status === 20) return 'rejected'
            return 'pending'
        }
    }
}
</script>
<style lang="scss" scoped>
.orderDetail {
    min-height: 100vh;
    background: #fff;
}
.totalPrice {
    text-align: right;
}
.orderList {
    width: 100%;
    background: #F4F4F4;
    border-radius: .5rem;
    padding: 1rem;
    box-sizing: border-box;
    margin: 1rem 0;
    font-size: .875rem;
}
.orderData {
    width: 100%;
    padding: 1rem;
    box-sizing: border-box;
    .title {
        font-size: .875rem;
        color: #666666;
        margin-bottom: 1rem;
    }
}
.refundMoneyBanner {
    background: #F5F5F5;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.875rem;
    color: #333;
}
// Refund Request InfoandPlatform Resolution区域样式
.refundSection {
    margin-bottom: 1.5rem;
    .sectionTitle {
        font-size: .875rem;
        color: #333;
        font-weight: 500;
        margin-bottom: 0.75rem;
    }
}
// Refund Request Info框
.refundInfoBox {
    background: #F5F5F5;
    border-radius: 0.5rem;
    padding: 1rem;
    .refundInfoItem {
        margin-bottom: 1rem;
        &:last-child {
            margin-bottom: 0;
        }
        .label {
            font-size: 0.75rem;
            color: #999;
            margin-bottom: 0.5rem;
        }
        .value {
            font-size: 0.875rem;
            color: #333;
            line-height: 1.5;
        }
        &.imageItem {
            .imageList {
                display: flex;
                gap: 0.5rem;
                margin-top: 0.25rem;
                .van-image {
                    border-radius: 0.25rem;
                    overflow: hidden;
                }
                .emptyImage {
                    width: 60px;
                    height: 60px;
                    background: #E5E5E5;
                    border-radius: 0.25rem;
                }
            }
        }
    }
}
// 处理结果框
.resultBox {
    background: #F5F5F5;
    border-radius: 0.5rem;
    padding: 1rem;
    .resultText {
        font-size: 0.875rem;
        line-height: 1.5;
        &.pending {
            color: #FF8C00;
        }
        &.approved {
            color: #52c41a;
        }
        &.shop-approved {
            color: #00AD56;
        }
        &.rejected {
            color: #ff4d4f;
        }
    }
    .resultDesc {
        font-size: 0.75rem;
        color: #666;
        margin-top: 0.5rem;
        line-height: 1.5;
    }
    .refundMoney {
        font-size: 0.875rem;
        color: #333;
        margin-top: 0.5rem;
    }
}
.orderInfo {
    width: 100%;
    height: auto;
    font-size: .875rem;
    .infoItem {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.5rem;
        &:last-child {
            margin-bottom: 0;
        }
    }
    .imageItem {
        flex-direction: column;
        align-items: flex-start;
        .imageList {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-top: 0.5rem;
            .van-image {
                border-radius: 0.25rem;
                overflow: hidden;
            }
        }
    }
    .orderListInfo {
        width: 100%;
        padding: 1rem;
        box-sizing: border-box;
        background: #EEEEEE;
        border-radius: .5rem;
        font-size: 12px;
        color: #666666;
        line-height: 1.5rem;
    }
}
</style>
